import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

export function DriverPicker({ drivers, customers = [], terminals = [], value, onChange, allowManual = true, noneLabel = null, groupBy = "company", testid = "report-driver-select" }) {
  const termName = (d) => d.terminal_name || terminals.find((t) => t.id === d.terminal_id)?.name || "";
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => {
    const name = (d) => {
      if (groupBy !== "company") return termName(d) || "Drivers";
      const c = customers.find((x) => x.id === d.customer_id);
      return c ? (c.company || c.name) : "Safer Highways Inc";
    };
    const m = new Map();
    for (const d of drivers) { const k = name(d); if (!m.has(k)) m.set(k, []); m.get(k).push(d); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, list]) => [label, list.sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`))]);
  }, [drivers, customers, groupBy]);
  const current = drivers.find((d) => d.id === value);
  const single = groups.length <= 1;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">{current ? `${current.first_name} ${current.last_name} · CDL ${current.license_number || "—"}${!single ? ` · ${groups.find(([, l]) => l.includes(current))?.[0] || ""}` : ""}` : value === "" && allowManual ? "— Enter name manually (new driver) —" : value === "" && noneLabel ? noneLabel : "Search drivers…"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(v, s) => (v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Type a name, CDL #, terminal or company…" data-testid="driver-picker-search" />
          <CommandList className="max-h-72">
            <CommandEmpty><span className="flex items-center gap-2 text-slate-500"><Search className="h-4 w-4" />No driver matches</span></CommandEmpty>
            {allowManual && (
              <CommandGroup>
                <CommandItem value="__manual__ enter name manually new driver" onSelect={() => { onChange(""); setOpen(false); }} data-testid="driver-picker-manual"><UserPlus className="mr-2 h-4 w-4 text-blue-700" />Enter name manually — adds a new driver</CommandItem>
              </CommandGroup>
            )}
            {noneLabel && !allowManual && (
              <CommandGroup>
                <CommandItem value="__none__ company level none" onSelect={() => { onChange(""); setOpen(false); }} data-testid="driver-picker-none"><Check className={`mr-2 h-4 w-4 ${value === "" ? "opacity-100" : "opacity-0"}`} />{noneLabel}</CommandItem>
              </CommandGroup>
            )}
            {groups.map(([label, list]) => (
              <CommandGroup key={label} heading={single ? undefined : `${label} (${list.length})`}>
                {list.map((d) => (
                  <CommandItem key={d.id} value={`${d.first_name} ${d.last_name} ${d.license_number || ""} ${termName(d)} ${label}`} onSelect={() => { onChange(d.id); setOpen(false); }} data-testid={`driver-picker-item-${d.id}`}>
                    <Check className={`mr-2 h-4 w-4 ${value === d.id ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1 truncate">{d.first_name} {d.last_name}{termName(d) ? <span className="ml-1.5 text-xs text-slate-400">· {termName(d)}</span> : null}</span>
                    <span className="ml-2 font-mono text-xs text-slate-500">{d.license_state ? `${d.license_state} ` : ""}{d.license_number || ""}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
